import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  User,
  MapPin,
  CreditCard,
  Package,
  Truck,
  Download,
  FileText,
  Printer,
  Clock,
  Check,
  CheckCircle,
  XCircle,
  MessageCircle,
} from 'lucide-react';
import { ordersApi } from '../../api/endpoints/orders';
import { shippingApi } from '../../api/endpoints/shipping';
import { Select } from '../../components/ui/Select';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Badge } from '../../components/ui/Badge';
import { CurrencyDisplay } from '../../components/ui/CurrencyDisplay';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Alert';
import { formatDateTime, getCdnUrl } from '../../lib/formatters';
import { useStore } from '../../contexts/StoreContext';
import { ORDER_STATUSES, DELIVERY_STATUSES, PAYMENT_STATUSES, SHIPPING_STATUSES } from '../../lib/constants';
import { OrderItemFields } from './OrderItemFields';
import type { OrderStatus, PaymentStatus, ShippingStatus } from '../../types/order';

export function OrderDetails() {
  const { storeId, orderId } = useParams<{ storeId: string; orderId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentStore } = useStore();

  const [trackingInput, setTrackingInput] = useState('');
  const [shippingCompanyInput, setShippingCompanyInput] = useState('');
  const [isEditingTracking, setIsEditingTracking] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  // Shipping label creation flow
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [labelStep, setLabelStep] = useState<'weight' | 'carriers' | 'creating'>('weight');
  const [shipmentWeight, setShipmentWeight] = useState('1.0');
  const [boxCount, setBoxCount] = useState(1);
  const [availableCarriers, setAvailableCarriers] = useState<Array<{
    id: number; title: string; title_arabic?: string; carrier_logo?: string;
    rate?: number; approximate_delivery_time_arabic?: string; active?: boolean; warning?: string;
  }>>([]);
  const [carriersLoading, setCarriersLoading] = useState(false);
  const [selectedCarrierId, setSelectedCarrierId] = useState<number | null>(null);
  const [creatingLabel, setCreatingLabel] = useState(false);

  const { data: order, isLoading } = useQuery({
    queryKey: ['order', storeId, orderId],
    queryFn: () => ordersApi.get(storeId!, orderId!),
    enabled: !!storeId && !!orderId,
  });

  // Backend marks the order seen during GET /:orderId. Once that fetch
  // resolves, the cached orders list still has seen:false — invalidate so
  // the "New" badge and unseen counter clear when the user goes back.
  useEffect(() => {
    if (!order?._id) return;
    queryClient.invalidateQueries({ queryKey: ['orders', storeId] });
    queryClient.invalidateQueries({ queryKey: ['orders-unseen', storeId] });
    queryClient.invalidateQueries({ queryKey: ['orders-status-counts', storeId] });
  }, [order?._id, queryClient, storeId]);

  const updateMutation = useMutation({
    mutationFn: (data: { status?: OrderStatus; paymentStatus?: PaymentStatus; delivery?: string; shippingStatus?: string; trackingNumber?: string; shippingCarrier?: string }) =>
      ordersApi.update(storeId!, orderId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', storeId, orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders', storeId] });
      toast('success', t('orders.updateSuccess'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const bankTransferMutation = useMutation({
    mutationFn: (action: 'approve' | 'reject') =>
      ordersApi.reviewBankTransfer(storeId!, orderId!, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['order', storeId, orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders', storeId] });
      toast('success', t('orders.updateSuccess'));
    },
    onError: () => toast('error', t('common.error')),
  });

  // Compute timeline from order activity log (with fallback for old orders)
  const timeline = useMemo(() => {
    if (!order) return [];

    const iconMap: Record<string, React.ReactNode> = {
      created: <Clock className="h-4 w-4" />,
      payment_status_changed: <CreditCard className="h-4 w-4" />,
      delivery_status_changed: <Check className="h-4 w-4" />,
      tracking_added: <Package className="h-4 w-4" />,
      note_updated: <FileText className="h-4 w-4" />,
      address_updated: <MapPin className="h-4 w-4" />,
      shipping_carrier_changed: <Truck className="h-4 w-4" />,
      bank_transfer_approved: <CreditCard className="h-4 w-4" />,
      bank_transfer_rejected: <CreditCard className="h-4 w-4" />,
    };

    const describeAction = (entry: { action: string; from?: string; to?: string; value?: string }) => {
      switch (entry.action) {
        case 'created': return t('orderTimeline.created');
        case 'payment_status_changed': return t('orderTimeline.paymentChanged', { status: t(`orders.${entry.to}`, entry.to ?? '') });
        case 'delivery_status_changed': return t('orderTimeline.statusChanged', { status: t(`orders.${entry.to}`, entry.to ?? '') });
        case 'tracking_added': return `${t('orderTimeline.trackingAdded')}: ${entry.value}`;
        case 'note_updated': return t('orderTimeline.noteAdded');
        case 'address_updated': return t('orderTimeline.addressUpdated', 'تم تحديث العنوان');
        case 'shipping_carrier_changed': return t('orderTimeline.shippingCarrierChanged', { carrier: entry.to }) || `شركة الشحن: ${entry.to}`;
        case 'bank_transfer_approved': return t('orderTimeline.bankTransferApproved', 'تم قبول التحويل البنكي');
        case 'bank_transfer_rejected': return t('orderTimeline.bankTransferRejected', 'تم رفض التحويل البنكي');
        default: return entry.action;
      }
    };

    // Use real activity log if available
    if (order.activity && order.activity.length > 0) {
      return order.activity.map(entry => ({
        icon: iconMap[entry.action] || <Clock className="h-4 w-4" />,
        description: describeAction(entry),
        timestamp: entry.at,
      }));
    }

    // Fallback for old orders without activity log
    const entries: { icon: React.ReactNode; description: string; timestamp: string }[] = [];
    entries.push({ icon: <Clock className="h-4 w-4" />, description: t('orderTimeline.created'), timestamp: order.createdAt });
    const displayStatus = order.delivery || order.status;
    entries.push({ icon: <Check className="h-4 w-4" />, description: t('orderTimeline.statusChanged', { status: t(`orders.${displayStatus}`, displayStatus) }), timestamp: order.createdAt });
    entries.push({ icon: <CreditCard className="h-4 w-4" />, description: t('orderTimeline.paymentChanged', { status: t(`orders.${order.paymentStatus}`, order.paymentStatus) }), timestamp: order.createdAt });
    return entries;
  }, [order, t]);

  const handleDownloadInvoice = async () => {
    if (!storeId || !orderId) return;
    setDownloadingInvoice(true);
    try {
      const blob = await ordersApi.downloadInvoice(storeId, orderId);
      const filename = `invoice-${displayNumber || orderId}.pdf`;
      if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
        try {
          const file = new File([blob], filename, { type: 'application/pdf' });
          await navigator.share({ files: [file], title: t('orders.downloadInvoice', 'Invoice') });
          return;
        } catch {
          // Share cancelled or failed - fall back to download
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast('error', t('common.error'));
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const handleSaveTracking = () => {
    if (!trackingInput.trim()) return;
    updateMutation.mutate(
      { trackingNumber: trackingInput.trim(), shippingCarrier: shippingCompanyInput.trim() || undefined },
      {
        onSuccess: () => {
          setIsEditingTracking(false);
        },
      },
    );
  };

  const openLabelModal = () => {
    setShowLabelModal(true);
    setLabelStep('weight');
    setShipmentWeight('1.0');
    setBoxCount(1);
    setAvailableCarriers([]);
    setSelectedCarrierId(null);
  };

  const fetchCarriers = async () => {
    setCarriersLoading(true);
    try {
      const result = await shippingApi.listAvailableCarriers(storeId!, {
        orderId,
        weight: parseFloat(shipmentWeight) || 1.0,
        boxCount,
      });
      setAvailableCarriers(result.companies || []);
      setLabelStep('carriers');
    } catch {
      toast('error', t('common.error'));
    } finally {
      setCarriersLoading(false);
    }
  };

  const handleCreateLabel = async () => {
    if (!selectedCarrierId) return;
    setCreatingLabel(true);
    try {
      const result = await shippingApi.createLabel(storeId!, {
        orderId: orderId!,
        carrierId: String(selectedCarrierId),
        weight: parseFloat(shipmentWeight) || 1.0,
        boxCount,
      });
      queryClient.invalidateQueries({ queryKey: ['order', storeId, orderId] });
      queryClient.invalidateQueries({ queryKey: ['orders', storeId] });
      toast('success', t('orders.labelCreated', 'Shipping label created'));
      setShowLabelModal(false);
      if (result.shippingLabel) {
        window.open(result.shippingLabel, '_blank');
      }
    } catch {
      toast('error', t('common.error'));
    } finally {
      setCreatingLabel(false);
    }
  };

  if (isLoading) return <LoadingSpinner fullPage />;
  if (!order) return null;

  const currency = order.currency || currentStore?.currency || 'SAR';
  const displayNumber = order.orderNumber ?? order._id?.slice?.(-6) ?? '';
  const isDigitalOrder = order.allItemsAreAutoDelivered
    || (order.items.length > 0 && order.items.every(i => i.productType === 'code' || i.productType === 'file' || i.productType === 'digital'));
  const showTrackingForm = !order.trackingId || isEditingTracking;

  return (
    <div className="mx-auto max-w-4xl">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/${storeId}/orders`)}
            className="rounded-lg p-2 text-text-muted hover:bg-bg-hover hover:text-text-primary"
          >
            <ArrowRight className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold">
              {t('orders.orderDetails')} #{displayNumber}
            </h1>
            <p className="text-sm text-text-muted">{formatDateTime(order.createdAt)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleDownloadInvoice}
            isLoading={downloadingInvoice}
          >
            <Download className="h-4 w-4" />
            {t('orders.downloadInvoice', 'Invoice')}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => window.print()}>
            <Printer className="h-4 w-4" />
            {t('orderTimeline.printOrder')}
          </Button>
          <StatusBadge status={order.delivery || order.status} />
          <StatusBadge status={order.paymentStatus} />
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Items */}
          <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
            <div className="mb-4 flex items-center justify-between gap-2">
              <h2 className="flex items-center gap-2 text-base font-semibold">
                <Package className="h-5 w-5 text-accent" />
                {t('orders.items')}
              </h2>
            </div>
            <div className="space-y-3">
              {(order.items || []).map((item, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-border-default/50 bg-bg-tertiary p-3"
                >
                  <div className="flex items-center gap-3">
                    {item.image ? (
                      <img
                        src={getCdnUrl(item.image, currentStore?._id)}
                        alt=""
                        className="h-12 w-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-lg bg-bg-hover" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      {item.variantTitle && (
                        <p className="text-xs text-text-muted">{item.variantTitle}</p>
                      )}
                      {item.sku && <p className="text-xs text-text-muted">SKU: {item.sku}</p>}
                      <OrderItemFields fields={item.fields} />
                    </div>
                    <div className="text-end">
                      <p className="text-sm">
                        <CurrencyDisplay amount={item.price} currency={currency} /> x {item.quantity}
                      </p>
                      <p className="text-sm font-medium">
                        <CurrencyDisplay amount={item.total} currency={currency} />
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Summary */}
            <div className="mt-4 space-y-2 border-t border-border-default pt-4">
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary">{t('orders.subtotal')}</span>
                <CurrencyDisplay amount={order.subtotal} currency={currency} />
              </div>
              {order.discount != null && order.discount > 0 && (
                <div className="flex justify-between text-sm text-success">
                  <span>{t('orders.discount')}</span>
                  <span>-<CurrencyDisplay amount={order.discount} currency={currency} /></span>
                </div>
              )}
              {order.shippingCost != null && order.shippingCost > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">{t('orders.shipping')}</span>
                  <CurrencyDisplay amount={order.shippingCost} currency={currency} />
                </div>
              )}
              {order.tax != null && order.tax > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">{t('orders.tax')}</span>
                  <CurrencyDisplay amount={order.tax} currency={currency} />
                </div>
              )}
              <div className="flex justify-between text-base font-bold border-t border-border-default pt-2">
                <span>{t('common.total')}</span>
                <CurrencyDisplay amount={order.total} currency={currency} />
              </div>
            </div>
          </section>

          {/* Notes */}
          {order.notes && (
            <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
              <h2 className="text-base font-semibold mb-2">{t('orders.notes')}</h2>
              <p className="text-sm text-text-secondary whitespace-pre-wrap">{order.notes}</p>
            </section>
          )}

          {/* Order Timeline */}
          <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
            <h2 className="flex items-center gap-2 text-base font-semibold mb-4">
              <Clock className="h-5 w-5 text-accent" />
              {t('orderTimeline.created', 'Order Timeline')}
            </h2>
            <div className="relative">
              {timeline.map((entry, i) => (
                <div key={i} className="relative flex gap-3 pb-6 last:pb-0">
                  {/* Connector line */}
                  {i < timeline.length - 1 && (
                    <div className="absolute start-[15px] top-8 bottom-0 w-px bg-border-default" />
                  )}
                  {/* Dot */}
                  <div className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border-default bg-bg-tertiary text-text-secondary">
                    {entry.icon}
                  </div>
                  {/* Content */}
                  <div className="flex-1 pt-1">
                    <p className="text-sm text-text-primary">{entry.description}</p>
                    <p className="text-xs text-text-muted mt-0.5">{formatDateTime(entry.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Status updates */}
          <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
            <h2 className="text-base font-semibold">{t('orders.updateStatus')}</h2>
            <Select
              label={t('common.status')}
              options={DELIVERY_STATUSES.map((s) => ({ value: s, label: t(`orders.${s}`, s) }))}
              value={order.delivery || order.status}
              onChange={(e) => updateMutation.mutate({ delivery: e.target.value })}
            />
            {order.paymentMethod === 'bankTransfer' ? (
              <Select
                label={t('orders.paymentStatus')}
                options={PAYMENT_STATUSES.map((s) => ({ value: s, label: t(`orders.${s}`, s) }))}
                value={order.paymentStatus}
                onChange={(e) => updateMutation.mutate({ paymentStatus: e.target.value as PaymentStatus })}
              />
            ) : (
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">{t('orders.paymentStatus')}</label>
                <div className="px-3 py-2 bg-bg-secondary rounded-lg text-sm text-text-primary">{t(`orders.${order.paymentStatus}`, order.paymentStatus)}</div>
              </div>
            )}
            {!isDigitalOrder && (
              <Select
                label={t('orders.shippingStatus')}
                options={SHIPPING_STATUSES.map((s) => ({ value: s, label: t(`orders.${s}`, s) }))}
                value={order.shippingStatus}
                onChange={(e) => updateMutation.mutate({ shippingStatus: e.target.value })}
              />
            )}
          </section>

          {/* Shipping / Tracking - hidden for digital-only orders */}
          {!isDigitalOrder && <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
            <h2 className="flex items-center gap-2 text-base font-semibold mb-3">
              <Truck className="h-5 w-5 text-accent" />
              {t('orders.shipping')}
            </h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-text-secondary">{t('orders.shippingStatus')}</span>
                <StatusBadge status={order.shippingStatus} />
              </div>
              {order.trackingId && !isEditingTracking && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">{t('orderTimeline.trackingNumber')}</span>
                  <button
                    onClick={() => {
                      setTrackingInput(order.trackingId || '');
                      setShippingCompanyInput(order.shippingCompany || '');
                      setIsEditingTracking(true);
                    }}
                    className="text-sm font-mono text-accent hover:underline"
                  >
                    {order.trackingId}
                  </button>
                </div>
              )}
              {order.shippingCompany && !isEditingTracking && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">{t('orders.shippingCarrier')}</span>
                  <span className="text-sm">{order.shippingCompany}</span>
                </div>
              )}

              {/* Tracking Number Input */}
              {showTrackingForm && (
                <div className="space-y-2 border-t border-border-default pt-3">
                  <Input
                    label={t('orderTimeline.trackingNumber')}
                    placeholder={t('orderTimeline.enterTracking')}
                    value={trackingInput}
                    onChange={(e) => setTrackingInput(e.target.value)}
                  />
                  <Input
                    label={t('orders.shippingCarrier')}
                    value={shippingCompanyInput}
                    onChange={(e) => setShippingCompanyInput(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      className="flex-1"
                      onClick={handleSaveTracking}
                      isLoading={updateMutation.isPending}
                      disabled={!trackingInput.trim()}
                    >
                      {t('orderTimeline.saveTracking')}
                    </Button>
                    {isEditingTracking && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setIsEditingTracking(false)}
                      >
                        {t('common.cancel', 'Cancel')}
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {order.shippingLabel && (
                <a
                  href={order.shippingLabel}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 mt-2"
                >
                  <Button variant="secondary" size="sm" className="w-full">
                    <Download className="h-4 w-4" />
                    {t('orders.downloadLabel')}
                  </Button>
                </a>
              )}
              {!order.shippingLabel && order.shippingAddress && ['pending', 'processing', 'completed'].includes(order.status) && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="w-full mt-2"
                  onClick={openLabelModal}
                >
                  <FileText className="h-4 w-4" />
                  {t('orders.createLabel')}
                </Button>
              )}
            </div>
          </section>}

          {/* Customer */}
          {order.customer && (
            <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
              <h2 className="flex items-center gap-2 text-base font-semibold mb-3">
                <User className="h-5 w-5 text-accent" />
                {t('orders.customer')}
              </h2>
              <div className="space-y-1 text-sm">
                {order.customer.name && <p>{order.customer.name}</p>}
                {order.customer.email && <p className="text-text-muted">{order.customer.email}</p>}
                {order.customer.phone && (
                  <p className="text-text-muted flex items-center gap-2">
                    {order.customer.phone}
                    <a
                      href={`https://wa.me/${order.customer.phone.replace(/\+/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={t('orders.whatsapp', 'WhatsApp')}
                      className="inline-flex text-[#25D366] hover:opacity-80"
                    >
                      <MessageCircle className="h-4 w-4" />
                    </a>
                  </p>
                )}
              </div>
            </section>
          )}

          {/* Shipping address - hidden for digital-only orders */}
          {!isDigitalOrder && order.shippingAddress && (
            <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
              <h2 className="flex items-center gap-2 text-base font-semibold mb-3">
                <MapPin className="h-5 w-5 text-accent" />
                {t('orders.shippingAddress')}
              </h2>
              <div className="space-y-1 text-sm text-text-secondary">
                {order.shippingAddress.name && <p>{order.shippingAddress.name}</p>}
                {order.shippingAddress.street && <p>{order.shippingAddress.street}</p>}
                <p>
                  {[order.shippingAddress.city, order.shippingAddress.state, order.shippingAddress.zipCode]
                    .filter(Boolean)
                    .join(', ')}
                </p>
                {order.shippingAddress.country && <p>{order.shippingAddress.country}</p>}
                {order.shippingAddress.phone && <p>{order.shippingAddress.phone}</p>}
              </div>
            </section>
          )}

          {/* Payment */}
          <section className="rounded-xl border border-border-default bg-bg-secondary p-6">
            <h2 className="flex items-center gap-2 text-base font-semibold mb-3">
              <CreditCard className="h-5 w-5 text-accent" />
              {t('orders.paymentStatus')}
            </h2>
            <StatusBadge status={order.paymentStatus} />
            {order.couponCode && (
              <p className="mt-2 text-sm text-text-muted">
                {t('orders.discount')}: <code className="text-accent">{order.couponCode}</code>
              </p>
            )}

            {/* Bank Transfer Review */}
            {order.paymentMethod === 'bankTransfer' && (
              <div className="mt-4 border-t border-border-default pt-4 space-y-3">
                <p className="text-sm font-medium">{t('orders.bankTransfer', 'Bank Transfer')}</p>

                {order.bankTransferReceipt && (
                  <img
                    src={getCdnUrl(order.bankTransferReceipt, currentStore?._id, 'products', 1280)}
                    alt={t('orders.receipt', 'Receipt')}
                    className="w-full max-h-64 rounded-lg object-contain border border-border-default cursor-pointer"
                    onClick={() => window.open(getCdnUrl(order.bankTransferReceipt!, currentStore?._id, 'products', null), '_blank')}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                )}

                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                    order.bankTransferStatus === 'approved'
                      ? 'bg-success/15 text-success'
                      : order.bankTransferStatus === 'rejected'
                        ? 'bg-danger/15 text-danger'
                        : 'bg-warning/15 text-warning'
                  }`}>
                    {order.bankTransferStatus === 'approved'
                      ? t('orders.transferApproved', 'Approved')
                      : order.bankTransferStatus === 'rejected'
                        ? t('orders.transferRejected', 'Rejected')
                        : t('orders.transferPending', 'Pending Review')}
                  </span>
                </div>

                {order.bankTransferStatus === 'pending' && (
                  <div className="flex gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      className="flex-1"
                      onClick={() => bankTransferMutation.mutate('approve')}
                      isLoading={bankTransferMutation.isPending}
                    >
                      <CheckCircle className="h-4 w-4" />
                      {t('orders.approveTransfer', 'Approve')}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex-1 border-danger/30 text-danger hover:bg-danger/5"
                      onClick={() => bankTransferMutation.mutate('reject')}
                      isLoading={bankTransferMutation.isPending}
                    >
                      <XCircle className="h-4 w-4" />
                      {t('orders.rejectTransfer', 'Reject')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </section>

        </div>
      </div>

      {/* Shipping Label Creation Modal */}
      <Modal
        isOpen={showLabelModal}
        onClose={() => setShowLabelModal(false)}
        title={t('orders.createLabel')}
        size="lg"
        footer={
          labelStep === 'weight' ? (
            <>
              <Button variant="secondary" onClick={() => setShowLabelModal(false)}>{t('common.cancel')}</Button>
              <Button onClick={fetchCarriers} isLoading={carriersLoading}>
                {t('common.next', 'Next')}
              </Button>
            </>
          ) : labelStep === 'carriers' ? (
            <>
              <Button variant="secondary" onClick={() => setLabelStep('weight')}>{t('common.back', 'Back')}</Button>
              <Button onClick={handleCreateLabel} isLoading={creatingLabel} disabled={!selectedCarrierId}>
                <Truck className="h-4 w-4" />
                {t('orders.createLabel')}
              </Button>
            </>
          ) : null
        }
      >
        {labelStep === 'weight' && (
          <div className="space-y-4">
            {/* Payment badge */}
            {order.paymentStatus === 'pending' && (
              <div className="rounded-lg bg-warning/10 border border-warning/20 p-3 text-sm text-warning">
                {t('orders.codWarning', 'COD: Settlement may take 7-14 business days')}
              </div>
            )}
            <Input
              label={t('orders.shipmentWeight', 'Shipment Weight (kg)')}
              type="number"
              step="0.1"
              min="0.1"
              value={shipmentWeight}
              onChange={(e) => setShipmentWeight(e.target.value)}
              placeholder="1.0"
            />
            <Input
              label={t('orders.boxCount', 'Number of Boxes')}
              type="number"
              min="1"
              value={boxCount}
              onChange={(e) => setBoxCount(Math.max(1, Number(e.target.value) || 1))}
            />
          </div>
        )}

        {labelStep === 'carriers' && (
          <div className="space-y-3">
            {availableCarriers.length === 0 ? (
              <p className="text-sm text-text-muted text-center py-6">{t('orders.noCarriers', 'No shipping companies available')}</p>
            ) : (
              availableCarriers.map((carrier) => (
                <button
                  key={carrier.id}
                  type="button"
                  onClick={() => setSelectedCarrierId(carrier.id)}
                  className={`flex w-full items-center gap-4 rounded-lg border p-4 text-start transition-colors ${
                    selectedCarrierId === carrier.id
                      ? 'border-accent bg-accent/5'
                      : 'border-border-default hover:border-accent/50 hover:bg-bg-hover'
                  }`}
                >
                  {carrier.carrier_logo ? (
                    <img src={carrier.carrier_logo} alt="" className="h-10 w-10 rounded-lg object-contain" />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-bg-tertiary">
                      <Truck className="h-5 w-5 text-text-muted" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{carrier.title_arabic || carrier.title}</p>
                    {carrier.approximate_delivery_time_arabic && (
                      <p className="text-xs text-text-muted">{carrier.approximate_delivery_time_arabic}</p>
                    )}
                    {carrier.warning && (
                      <p className="text-xs text-warning mt-0.5">{carrier.warning}</p>
                    )}
                  </div>
                  {carrier.rate != null && (
                    <div className="text-end shrink-0">
                      <CurrencyDisplay amount={carrier.rate} currency={currency} className="font-semibold" />
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        )}

        {labelStep === 'creating' && (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner />
          </div>
        )}
      </Modal>
    </div>
  );
}
